import { StringToUInt8, UInt8ToString } from "@pdfking/tfunc"
import { RenameMap } from "../maps/RenameMap"

/** Minify import/export names in a WASM binary using RenameMap. */
export function RenameWasm(
  buf: Uint8Array,
  rename = (name: string): string => RenameMap[name] ?? name
): Uint8Array {
  const chunks: Uint8Array[] = []
  let pos = 0

  const readU8 = () => buf[pos++]
  const readLeb128 = () => {
    let result = 0
    let shift = 0
    let byte: number
    do {
      byte = buf[pos++]
      result |= (byte & 0x7f) << shift
      shift += 7
    } while (byte & 0x80)
    return result
  }
  const readBytes = (n: number) => buf.subarray(pos, (pos += n))
  const readStr = () => UInt8ToString(readBytes(readLeb128()))

  const encodeLeb128 = (value: number): Uint8Array => {
    const bytes: number[] = []
    do {
      let byte = value & 0x7f
      value >>>= 7
      if (value) byte |= 0x80
      bytes.push(byte)
    } while (value)
    return new Uint8Array(bytes)
  }

  const encodeStr = (s: string): Uint8Array => {
    const b = StringToUInt8(s)
    const len = encodeLeb128(b.length)
    const out = new Uint8Array(len.length + b.length)
    out.set(len)
    out.set(b, len.length)
    return out
  }

  const push = (...parts: (Uint8Array | number[])[]) => {
    for (const p of parts)
      chunks.push(p instanceof Uint8Array ? p : new Uint8Array(p))
  }

  // Magic + version
  push(readBytes(8))

  while (pos < buf.length) {
    const sectionId = readU8()
    const sectionLen = readLeb128()

    if (sectionId === 2) {
      // Import section
      const body: (Uint8Array | number[])[] = []
      const count = readLeb128()
      body.push(encodeLeb128(count))
      for (let i = 0; i < count; i++) {
        const mod = readStr()
        const field = readStr()
        const kind = readU8()
        body.push(encodeStr(rename(mod)))
        body.push(encodeStr(rename(field)))
        body.push([kind])
        if (kind === 0) {
          const start = pos
          readLeb128()
          body.push(buf.slice(start, pos))
        } else if (kind === 1) {
          body.push([readU8()])
          const flags = readU8()
          body.push([flags])
          const start = pos
          readLeb128()
          if (flags & 1) readLeb128()
          body.push(buf.slice(start, pos))
        } else if (kind === 2) {
          const flags = readU8()
          body.push([flags])
          const start = pos
          readLeb128()
          if (flags & 1) readLeb128()
          body.push(buf.slice(start, pos))
        } else if (kind === 3) {
          body.push([readU8(), readU8()])
        }
      }
      const bodyLen = body.reduce((s, c) => s + c.length, 0)
      push([sectionId], encodeLeb128(bodyLen), ...body)
    } else if (sectionId === 7) {
      // Export section
      const body: (Uint8Array | number[])[] = []
      const count = readLeb128()
      body.push(encodeLeb128(count))
      for (let i = 0; i < count; i++) {
        const name = readStr()
        const kind = readU8()
        const start = pos
        readLeb128()
        body.push(encodeStr(rename(name)))
        body.push([kind])
        body.push(buf.slice(start, pos))
      }
      const bodyLen = body.reduce((s, c) => s + c.length, 0)
      push([sectionId], encodeLeb128(bodyLen), ...body)
    } else {
      // Copy section as-is
      push([sectionId], encodeLeb128(sectionLen), readBytes(sectionLen))
    }
  }

  const totalLen = chunks.reduce((s, c) => s + c.length, 0)
  const result = new Uint8Array(totalLen)
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.length
  }

  return result
}
